'use client';
import { FC } from 'react';
import { Content, asText } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';
import HighlightedText from '@/components/HighlightedText/HighlightedText';

const HighlightedHeadline: FC<{ text: string }> = ({ text }) => (
  <h1 className="h1-hero text-center font-inter font-medium text-white">
    <HighlightedText text={text} />
  </h1>
);

export type HeadlineWithIllustrationProps =
  SliceComponentProps<Content.HeadlineWithIllustrationSlice>;

const HeadlineWithIllustration: FC<HeadlineWithIllustrationProps> = ({
  slice,
}) => {
  const { headline, description, illustration } = slice.primary;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-[#0E1010] py-10"
    >
      <div className="container mx-auto max-w-6xl text-center">
        {/* Headline with Green Highlights */}
        <div className="mb-10">
          {headline && <HighlightedHeadline text={asText(headline) || ''} />}

          <PrismicRichText
            field={description}
            components={{
              paragraph: ({ children }: { children: React.ReactNode }) => (
                <p className="text-gray-300 text-lg md:text-xl leading-relaxed max-w-4xl mx-auto">
                  {children}
                </p>
              ),
            }}
          />
        </div>

        {/* Illustration/Diagram Section */}
        <div className="px-2">
          {illustration ? (
            <div className="w-full px-4 sm:px-6 md:px-0">
              <PrismicNextImage
                field={illustration}
                className="mx-auto block w-full max-w-[490px] h-auto sm:h-[280px] md:h-[340px] lg:h-[385px]"
                priority
              />
            </div>
          ) : (
            /* Velx Workflow Diagram */
            <div className="relative max-w-4xl mx-auto">
              {/* Central Velx Logo */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
                <div className="w-24 h-24 bg-[#05F273] rounded-full flex items-center justify-center">
                  <span className="text-[#0E1010] font-bold text-xl">Velx</span>
                </div>
              </div>

              {/* Data Source (Left) */}
              <div className="absolute top-1/2 left-8 transform -translate-y-1/2">
                <div className="bg-[#05F273] rounded-lg p-4 w-20 h-24 flex flex-col items-center justify-center">
                  <div className="text-[#0E1010] font-semibold text-xs mb-1">
                    Data
                  </div>
                  <div className="w-full space-y-1">
                    <div className="h-1 bg-[#0E1010] rounded"></div>
                    <div className="h-1 bg-[#0E1010] rounded"></div>
                    <div className="h-1 bg-[#0E1010] rounded"></div>
                    <div className="h-1 bg-[#0E1010] rounded"></div>
                  </div>
                  <div className="flex space-x-1 mt-2">
                    <div className="w-1 h-1 bg-[#0E1010] rounded-full"></div>
                    <div className="w-1 h-1 bg-[#0E1010] rounded-full"></div>
                    <div className="w-1 h-1 bg-[#0E1010] rounded-full"></div>
                  </div>
                </div>
              </div>

              {/* AI-Based Intelligence (Bottom Right) */}
              <div className="absolute bottom-8 right-8">
                <div className="bg-white rounded-lg p-4 w-32 h-20 flex flex-col items-center justify-center shadow-lg">
                  <div className="text-gray-800 font-semibold text-xs mb-2">
                    AI-Based Intelligence
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                      <div className="w-3 h-3 bg-white rounded-sm"></div>
                    </div>
                    <div className="flex-1">
                      <div className="h-1 bg-yellow-400 rounded mb-1"></div>
                      <div className="h-1 bg-gray-300 rounded"></div>
                    </div>
                    <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                      <div className="w-2 h-1 bg-white rounded"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Connected Nodes */}
              <div className="absolute top-16 right-32">
                <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">CI</span>
                </div>
              </div>

              <div className="absolute top-32 right-16">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">DB</span>
                </div>
              </div>

              <div className="absolute bottom-32 left-32">
                <div className="w-6 h-6 bg-green-500 rounded-full"></div>
              </div>

              {/* Dotted Connection Lines */}
              <svg
                className="absolute inset-0 w-full h-full pointer-events-none"
                style={{ zIndex: 1 }}
              >
                {/* Data to Velx */}
                <line
                  x1="25%"
                  y1="50%"
                  x2="45%"
                  y2="50%"
                  stroke="#05F273"
                  strokeWidth="2"
                  strokeDasharray="5,5"
                />

                {/* Velx to AI Intelligence */}
                <line
                  x1="55%"
                  y1="60%"
                  x2="75%"
                  y2="80%"
                  stroke="#05F273"
                  strokeWidth="2"
                  strokeDasharray="5,5"
                />

                {/* Velx to top nodes */}
                <line
                  x1="55%"
                  y1="45%"
                  x2="75%"
                  y2="25%"
                  stroke="#05F273"
                  strokeWidth="2"
                  strokeDasharray="5,5"
                />

                {/* Velx to bottom left */}
                <line
                  x1="45%"
                  y1="55%"
                  x2="35%"
                  y2="75%"
                  stroke="#05F273"
                  strokeWidth="2"
                  strokeDasharray="5,5"
                />
              </svg>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default HeadlineWithIllustration;
